import { useState, useEffect } from 'react';
import type { CSSProperties } from 'react';
import { HUD_DESIGN } from './hudDesign';

const LOADING_ACCENT_RGB = '98,221,255';

function rgbaAlpha(alpha: number) {
  return `rgba(${LOADING_ACCENT_RGB},${alpha})`;
}

const LOADING_STYLE = {
  background: '#040a12',
  accent: HUD_DESIGN.neonBlue,
  accentDim: rgbaAlpha(0.12),
  accentTrack: rgbaAlpha(0.1),
  accentMedium: rgbaAlpha(0.3),
  accentTextMuted: rgbaAlpha(0.5),
  accentTextSubtle: rgbaAlpha(0.35),
  accentTextDim: rgbaAlpha(0.55),
  success: '#00ff88',
  glowMid: rgbaAlpha(0.45),
  glowLow: rgbaAlpha(0.15),
  tipBackground: rgbaAlpha(0.06),
  tipBorder: rgbaAlpha(0.2),
  tipTitle: rgbaAlpha(0.55),
  tipText: '#dff8ff',
};

// Phase 166 — rotating gameplay tip-of-the-day list shown on the loading screen.
const GAMEPLAY_TIPS = [
  'Skiing downhill builds momentum — never walk a slope.',
  'Disc-jump with the spinfusor to reach high ledges and rooftops.',
  'Repair pack heals teammates, generators and turrets.',
  'Cut your jet in short bursts to conserve energy mid-flight.',
  'Lead your shot — discs are slow and arc with gravity.',
  'Defend the flag stand: returns score and deny the cap.',
  'Heavy armor carries the mortar; light armor chases the flag.',
  'Take out generators to disable enemy turrets and stations.',
  'Use the inventory station to re-arm and switch loadouts.',
  'Chain ski + jet to convert vertical speed into ground speed.',
];

export function resolveGameplayTip(index: number): string {
  return GAMEPLAY_TIPS[Math.abs(index) % GAMEPLAY_TIPS.length];
}

const FLAVOR_TEXTS = [
  'Initializing flux capacitors...',
  'Calibrating gravity drives...',
  'Loading tribesman data...',
  'Syncing arena telemetry...',
  'Charging plasma conduits...',
  'Establishing combat link...',
  'Mapping terrain gradients...',
  'Deploying sensor network...',
  'Priming jetpack systems...',
  'Engaging flag protocols...',
];

export function resolveLoadingFlavorText(index: number): string {
  return FLAVOR_TEXTS[Math.abs(index) % FLAVOR_TEXTS.length];
}

const loadingKeyframes = `
  @keyframes spin {
    to { transform: rotate(360deg); }
  }
  @keyframes dotPulse {
    0%, 100% { opacity: 0.2; }
    50% { opacity: 1; }
  }
  @keyframes fadeInUp {
    from { opacity: 0; transform: translateY(12px); }
    to { opacity: 1; transform: translateY(0); }
  }
  @keyframes pulseGlow {
    0%, 100% { box-shadow: 0 0 20px ${LOADING_STYLE.glowLow}; }
    50% { box-shadow: 0 0 40px ${LOADING_STYLE.glowMid}; }
  }
  @keyframes activePulse {
    0%, 100% { opacity: 1; }
    50% { opacity: 0.5; }
  }
`;

const overlayStyle: CSSProperties = {
  position: 'fixed',
  inset: 0,
  display: 'flex',
  flexDirection: 'column',
  alignItems: 'center',
  justifyContent: 'center',
  background: LOADING_STYLE.background,
  color: LOADING_STYLE.accent,
  zIndex: 200,
};

export interface LoadingStep {
  label: string;
  status: 'pending' | 'active' | 'done';
}

const DEFAULT_STEPS: LoadingStep[] = [
  { label: 'COMPILING SHADERS', status: 'done' },
  { label: 'CONNECTING TO SERVER', status: 'active' },
  { label: 'LOADING MAP', status: 'pending' },
  { label: 'JOINING MATCH', status: 'pending' },
];

interface LoadingScreenProps {
  steps?: LoadingStep[];
}

export function LoadingScreen({ steps: externalSteps }: LoadingScreenProps = {}) {
  const [flavorIndex, setFlavorIndex] = useState(0);
  const [tipIndex, setTipIndex] = useState(() => Math.floor(Math.random() * GAMEPLAY_TIPS.length));
  const [progress, setProgress] = useState(0);
  const [steps, setSteps] = useState<LoadingStep[]>(externalSteps ?? DEFAULT_STEPS);

  useEffect(() => {
    const interval = setInterval(() => {
      setFlavorIndex((prev) => (prev + 1) % FLAVOR_TEXTS.length);
    }, 2000);
    return () => clearInterval(interval);
  }, []);

  // Phase 166 — rotate the gameplay tip every ~4.5s so a player always sees a fresh one.
  useEffect(() => {
    const interval = setInterval(() => {
      setTipIndex((prev) => (prev + 1) % GAMEPLAY_TIPS.length);
    }, 4500);
    return () => clearInterval(interval);
  }, []);

  useEffect(() => {
    const start = Date.now();
    const duration = 3000;
    let frameId: number | null = null;

    const frame = () => {
      const elapsed = Date.now() - start;
      const nextProgress = Math.min(elapsed / duration, 1) * 100;
      setProgress((current) => (current === nextProgress ? current : nextProgress));

      if (nextProgress < 100) {
        frameId = window.requestAnimationFrame(frame);
      }
    };

    frameId = window.requestAnimationFrame(frame);
    return () => {
      if (frameId !== null) {
        window.cancelAnimationFrame(frameId);
      }
    };
  }, []);

  // Auto-advance steps if using default steps
  useEffect(() => {
    if (externalSteps) return;

    // Step 2 (index 1): active -> done after 800ms
    const t1 = setTimeout(() => {
      setSteps((prev) => {
        const next = [...prev];
        next[1] = { ...next[1], status: 'done' };
        next[2] = { ...next[2], status: 'active' };
        return next;
      });
    }, 800);

    // Step 3 (index 2): active -> done after 800+1200ms
    const t2 = setTimeout(() => {
      setSteps((prev) => {
        const next = [...prev];
        next[2] = { ...next[2], status: 'done' };
        next[3] = { ...next[3], status: 'active' };
        return next;
      });
    }, 2000);

    // Step 4 (index 3): active -> done after 800+1200+600ms
    const t3 = setTimeout(() => {
      setSteps((prev) => {
        const next = [...prev];
        next[3] = { ...next[3], status: 'done' };
        return next;
      });
    }, 2600);

    return () => {
      clearTimeout(t1);
      clearTimeout(t2);
      clearTimeout(t3);
    };
  }, [externalSteps]);

  return (
    <div style={overlayStyle}>
      <style>{loadingKeyframes}</style>
      <div
        style={{
          width: 72,
          height: 72,
          border: `4px solid ${LOADING_STYLE.accentDim}`,
          borderTopColor: LOADING_STYLE.accent,
          borderRightColor: LOADING_STYLE.accentMedium,
          borderRadius: '50%',
          animation: 'spin 0.8s linear infinite, pulseGlow 1.5s ease-in-out infinite',
        }}
        aria-hidden="true"
      />
      <div
        style={{ display: 'flex', gap: 8, marginTop: 20 }}
        aria-hidden="true"
      >
        {[0, 1, 2].map((i) => (
          <div
            key={i}
            style={{
              width: 6,
              height: 6,
              borderRadius: '50%',
              background: LOADING_STYLE.accent,
              animation: `dotPulse 1.4s ease-in-out infinite`,
              animationDelay: `${i * 0.25}s`,
              boxShadow: `0 0 8px ${LOADING_STYLE.accent}66`,
            }}
          />
        ))}
      </div>
      <div
        role="status"
        aria-live="polite"
        style={{
          marginTop: 28,
          fontSize: 14,
          letterSpacing: 4,
          textTransform: 'uppercase',
          color: LOADING_STYLE.accent,
          fontWeight: 700,
        }}
      >
        {'CONNECTING TO ARENA'}
        <span style={{ animation: 'dotPulse 1.2s ease-in-out infinite' }}>...</span>
      </div>
      <div
        aria-hidden="true"
        style={{
          marginTop: 20,
          width: 260,
          height: 3,
          background: LOADING_STYLE.accentTrack,
          borderRadius: 2,
          overflow: 'hidden',
        }}
      >
        <div
          style={{
            height: '100%',
            background: `linear-gradient(90deg, ${LOADING_STYLE.accent}, #00eeff)`,
            borderRadius: 2,
            width: `${progress}%`,
            transition: 'width 0.1s linear',
            boxShadow: `0 0 8px ${LOADING_STYLE.accentTextDim}`,
          }}
        />
      </div>

      {/* 4-step progress list */}
      <div style={{ marginTop: 28, display: 'flex', flexDirection: 'column', gap: 8 }}>
        {steps.map((step) => {
          const isDone = step.status === 'done';
          const isActive = step.status === 'active';
          const isPending = step.status === 'pending';
          return (
            <div
              key={step.label}
              style={{
                display: 'flex',
                alignItems: 'center',
                gap: 10,
                fontFamily: 'monospace',
                fontSize: 12,
                letterSpacing: 1,
              }}
            >
              <span style={{
                color: isDone
                  ? LOADING_STYLE.success
                  : isActive
                    ? LOADING_STYLE.accent
                    : LOADING_STYLE.accentTextSubtle,
                animation: isActive ? 'activePulse 0.8s ease-in-out infinite' : undefined,
                width: 14,
                textAlign: 'center',
              }}>
                {isDone ? '✓' : isActive ? '▶' : '○'}
              </span>
              <span
                style={{
                  color: isPending ? LOADING_STYLE.accentTextSubtle : '#ffffff',
                }}
              >
                {step.label}
              </span>
            </div>
          );
        })}
      </div>

      <div
        style={{
          marginTop: 24,
          fontSize: 11,
          letterSpacing: 2,
          textTransform: 'uppercase',
          color: LOADING_STYLE.accentTextMuted,
          animation: 'fadeInUp 0.6s ease-out 0.3s both',
        }}
      >
        {resolveLoadingFlavorText(flavorIndex)}
      </div>
      {/* Phase 166 — rotating tip-of-the-day. */}
      <div
        style={{
          marginTop: 28,
          maxWidth: 420,
          padding: '10px 16px',
          background: LOADING_STYLE.tipBackground,
          border: `1px solid ${LOADING_STYLE.tipBorder}`,
          textAlign: 'center',
          animation: 'fadeInUp 0.6s ease-out 0.6s both',
        }}
      >
        <div
          style={{ fontSize: 9, letterSpacing: 3, color: LOADING_STYLE.tipTitle, marginBottom: 4 }}
        >
          TIP
        </div>
        <div
          style={{ fontSize: 12, letterSpacing: 0.5, color: LOADING_STYLE.tipText, lineHeight: 1.4 }}
        >
          {resolveGameplayTip(tipIndex)}
        </div>
      </div>
    </div>
  );
}
